import { Streamdown } from 'streamdown';
import { cn } from '@/lib/utils';

/**
 * Markdown renderer for assistant messages. Streamdown handles incomplete
 * mid-stream markdown gracefully (open code fences, half-written tables).
 *
 * Two layout-critical things going on here:
 *  1. `shikiTheme` is forced to dark on both slots — Streamdown's default
 *     light theme produced a white code-block bubble in our otherwise-dark
 *     MiniApp.
 *  2. Aggressive width constraints (`min-w-0`, `max-w-full` on every
 *     descendant, `[overflow-wrap:anywhere]`, code blocks `overflow-x-auto`
 *     internally) so long URLs / paths / single-line code blocks never
 *     push the message bubble past its 88% max width.
 */
export function Response({ children, className }: { children: string; className?: string }) {
  return (
    <div
      className={cn(
        'min-w-0 max-w-full text-sm leading-relaxed [overflow-wrap:anywhere]',
        // Force every descendant to fit the parent. Prevents Streamdown's
        // wrappers (code blocks with copy/download controls, tables, etc.)
        // from establishing a wider intrinsic width than the bubble.
        '[&_*]:max-w-full [&_*]:min-w-0',
        // Headings + paragraphs
        '[&_h1]:mt-2 [&_h1]:text-base [&_h1]:font-semibold',
        '[&_h2]:mt-2 [&_h2]:text-sm [&_h2]:font-semibold',
        '[&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold',
        '[&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
        // Lists
        '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
        '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:my-0',
        // Inline code: wrap aggressively so long file paths break
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em] [&_code]:[overflow-wrap:anywhere] [&_code]:[word-break:break-all]',
        // Block code: scrolls inside, never overflows the bubble
        '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:bg-muted [&_pre]:p-2 [&_pre]:text-xs',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:break-normal [&_pre_code]:[overflow-wrap:normal] [&_pre_code]:[word-break:normal]',
        // Tables — make them scrollable in their wrapper
        '[&_table]:block [&_table]:overflow-x-auto',
        // Links + emphasis
        '[&_a]:text-primary [&_a]:underline-offset-2 hover:[&_a]:underline',
        '[&_strong]:font-semibold',
        className,
      )}
    >
      <Streamdown shikiTheme={['github-dark', 'github-dark']}>{children}</Streamdown>
    </div>
  );
}
